import React, { useState, useRef, useContext } from 'react';
import {
    Box,
    Button,
    Text,
    VStack,
    HStack,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Progress,
    Badge,
    Icon,
    useToast,
    Grid,
    GridItem,
    Tag,
    TagLabel,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Spinner,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Divider,
} from '@chakra-ui/react';
import { FiUploadCloud, FiFile, FiX, FiInfo, FiAlertCircle, FiDownload, FiClock, FiBarChart2 } from 'react-icons/fi';
import axios from 'axios';
import ImportPreflight from '../compnents/ImportPreflight';
import templateFile from '../assets/required_fields_template.xlsx';
import exampleFile from '../assets/required_fields_example.xlsx';
import AuthContext from '../context/AuthContext';
import { demoTrialSampleAssetPath, demoTrialSampleCount, demoTrialSampleFileName, demoDatasetSummary, demoSpotlightCustomers } from '../content/demoDataset';
import { useNavigate } from 'react-router-dom';

const isProduction = process.env.NODE_ENV === "production";
const API_BASE_URL = isProduction ? process.env.REACT_APP_API_BASE_URL_PROD : process.env.REACT_APP_API_BASE_URL_DEV;

function UploadYourFile({ token }) {
    const { user, refreshUser } = useContext(AuthContext);
    const [files, setFiles] = useState([]);
    const [preflight, setPreflight] = useState(null);
    const [isChecking, setIsChecking] = useState(false);
    const preflightRequest = useRef(0);
    const [isUploading, setIsUploading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [noOfFileUploads, setNoOfFileUploads] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [showRequiredFields, setShowRequiredFields] = useState(false);
    const [uploadedData, setUploadedData] = useState(null);
    const [uploadError, setUploadError] = useState(null);
    const [isLoadingDemoDataset, setIsLoadingDemoDataset] = useState(false);
    const fileInputRef = useRef(null);
    const toast = useToast();
    const navigate = useNavigate();
    const hasDatasetUploadAccess = ['solo', 'pro', 'business'].includes(user?.subscription);

    const redirectToTrial = () => {
        toast({
            title: 'Start a trial before uploading',
            description: 'The demo workspace is available now. Real customer datasets require Solo, Team or Business, or a 30-day trial.',
            status: 'info',
            duration: 4000,
            isClosable: true,
        });
        navigate('/subscription?trial=pro');
    };

    // Core required fields (compulsory)
    const coreRequiredFields = {
        'Customer Identification': [
            { name: 'customer_id', description: 'Unique customer identifier', required: true },
            { name: 'country_iso', description: 'Country code (3-letter ISO for compliance)', required: true },
            { name: 'age', description: 'Customer age', required: true },
        ],
        'Risk & Knowledge Assessment': [
            { name: 'risk_profile', description: 'Risk tolerance (1-10 scale)', required: true },
            { name: 'knowledge_quiz_score', description: 'Financial knowledge score (0-100)', required: true },
            { name: 'experience_years', description: 'Years of investment experience', required: true }
        ],
        'Portfolio Allocation': [
            { name: 'cash_pct', description: 'Cash percentage (0-100)', required: true },
            { name: 'etf_pct', description: 'ETF percentage (0-100)', required: true },
            { name: 'single_stock_pct', description: 'Individual stock percentage (0-100)', required: true },
        ],
    };

    // Optional fields for enhanced recommendations
    const optionalFields = {
        'Trading Behavior (Optional)': [
            { name: 'days_since_last_trade', description: 'Days since last trade', required: false },
            { name: 'stop_loss_sell_cnt', description: 'Stop-loss sell count', required: false },
            { name: 'days_since_loss_sell', description: 'Days since last loss realization', required: false },
            { name: 'leverage_ratio', description: 'Leverage usage ratio', required: false },
        ],
        'Investment Patterns (Optional)': [
            { name: 'home_country_weight_pct', description: 'Home country allocation (0-100)', required: false },
            { name: 'trade_turnover_30d_pct_peer', description: 'Trading turnover vs peers (0-100)', required: false },
            { name: 'anchor_price_gap_pct', description: 'Price gap from anchor price', required: false },
        ],
        'Order Types & Social (Optional)': [
            { name: 'limit_orders_at_purchase_px', description: 'Limit order usage (boolean)', required: false },
            { name: 'social_hype_score', description: 'Social media influence (0-100)', required: false },
            { name: 'days_since_hype_peak', description: 'Days since social hype peak', required: false },
        ],
        'News & Sentiment (Optional)': [
            { name: 'positive_news_sentiment_7d', description: 'News sentiment (-1 to 1)', required: false },
            { name: 'news_clicks_7d', description: 'News engagement count', required: false }
        ],
        'Gain/Loss Behavior (Optional)': [
            { name: 'gain_takes', description: 'Profit-taking count', required: false },
            { name: 'loss_takes', description: 'Loss realization count', required: false },
        ],
        'Portfolio Management (Optional)': [
            { name: 'rebalances_ytd', description: 'Rebalancing frequency', required: false },
            { name: 'num_sub_accounts', description: 'Number of sub-accounts', required: false },
            { name: 'averaging_down_trades', description: 'Dollar-cost averaging trades', required: false },
            { name: 'days_since_last_average_down', description: 'Days since last DCA', required: false },
            { name: 'max_drawdown_pct', description: 'Maximum drawdown percentage (-100 to 0)', required: false },
        ]
    };

    // Upload file to API
    const uploadFileToAPI = async (file, mapping = null) => {
        if (!hasDatasetUploadAccess) {
            redirectToTrial();
            return null;
        }

        const formData = new FormData();
        formData.append('file', file);
        // Import exactly the mapping that the preflight checked.
        if (mapping && Object.keys(mapping).length) formData.append('mapping', JSON.stringify(mapping));

        try {
            setIsUploading(true);
            setUploadProgress(0);
            setShowPreview(false);
            setUploadError(null);

            const response = await axios.post(`${API_BASE_URL}/content/upload-file`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`,
                },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    setUploadProgress(percentCompleted);
                },
            });

            // Handle response
            const data = response.data;
            setUploadedData(data);

            // Check if upload was successful or failed
            if (data.status === 'failed' && data.preview?.error) {
                setUploadError(data.preview.error);
                setShowPreview(true);
                toast({
                    title: 'Upload validation failed',
                    description: data.preview.error,
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
                await checkUserUploadCount();
            } else if (data.preview?.preview_rows && data.preview.preview_rows.length > 0) {
                setShowPreview(true);
                toast({
                    title: 'File uploaded successfully',
                    description: `Preview loaded with ${data.preview.preview_rows.length} rows.`,
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
                await checkUserUploadCount();
            } else {
                // If there's no preview but upload was successful
                toast({
                    title: 'File uploaded successfully',
                    description: 'Your file has been processed successfully.',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
                await checkUserUploadCount();
            }

            return data;
        } catch (error) {
            console.error('Upload error:', error);

            const errorMessage = error.response?.data?.message ||
                error.response?.data?.error ||
                'Failed to upload file. Please try again.';

            setUploadError(errorMessage);
            toast({
                title: 'Upload failed',
                description: errorMessage,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });

            setFiles([]);
            setUploadProgress(0);

            throw error;
        } finally {
            setIsUploading(false);
        }
    };

    const checkFileBeforeImport = async (file, mapping = null) => {
        const request = ++preflightRequest.current;
        if (!mapping) setPreflight(null);
        setIsChecking(true);
        setUploadError(null);
        try {
            const form = new FormData();
            form.append('file', file);
            if (mapping) form.append('mapping', JSON.stringify(mapping));
            const response = await axios.post(`${API_BASE_URL}/content/preflight-file`, form, {
                headers: {Authorization: `Bearer ${token}`}, timeout: 65000,
            });
            if (request === preflightRequest.current) setPreflight({file, report: response.data});
        } catch (error) {
            if (request === preflightRequest.current) setUploadError(error.response?.data?.message || 'Unable to check this file. Nothing has been imported.');
        } finally {
            if (request === preflightRequest.current) setIsChecking(false);
        }
    };

    // File upload handler
    const handleFileUpload = async (event) => {
        if (!hasDatasetUploadAccess) {
            redirectToTrial();
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            return;
        }

        const selectedFiles = Array.from(event.target.files);
        const validFiles = selectedFiles.filter(
            (file) =>
                file.name.endsWith('.csv') ||
                file.name.endsWith('.xlsx')
        );

        if (validFiles.length !== selectedFiles.length) {
            toast({
                title: 'Invalid file type',
                description: 'Please upload only CSV or Excel files',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }

        if (validFiles.length > 0) {
            const fileToUpload = validFiles[0];
            setFiles([fileToUpload]);
            await checkFileBeforeImport(fileToUpload);
        }
    };

    // Drag and drop handlers
    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        if (!hasDatasetUploadAccess) {
            redirectToTrial();
            return;
        }

        const droppedFiles = Array.from(e.dataTransfer.files);
        const validFiles = droppedFiles.filter(
            (file) =>
                file.name.endsWith('.csv') ||
                file.name.endsWith('.xlsx')
        );

        if (validFiles.length > 0) {
            const fileToUpload = validFiles[0];
            setFiles([fileToUpload]);
            await checkFileBeforeImport(fileToUpload);
        }
    };

    // Remove all files
    const handleRemoveAll = async () => {
        try {
            setIsDeleting(true);
            const response = await axios.get(`${API_BASE_URL}/content/remove-latest-uploaded-file`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            toast({
                title: 'File deleted',
                description: response.data?.message || 'The uploaded file was removed.',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            //Call check-user again to refresh noOfFileUploads
            await checkUserUploadCount();
            setFiles([]);
            setShowPreview(false);
            setUploadProgress(0);
            setUploadedData(null);
            setUploadError(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } catch (error) {
            console.error('Error removing file:', error);

            toast({
                title: 'Failed to delete file',
                description:
                    error.response?.data?.message ||
                    'An error occurred while deleting the file.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsDeleting(false);
        }
    };

    // Verify and continue handler
    const handleVerifyAndContinue = async () => {
        try {
            const refreshedUser = await checkUserUploadCount();

            if (refreshedUser) {
                toast({
                    title: 'Verification successful',
                    description: 'Redirecting to your dashboard...',
                    status: 'success',
                    duration: 2000,
                    isClosable: true,
                });
            } else {
                toast({
                    title: 'Unexpected response',
                    description: 'Please try again later.',
                    status: 'warning',
                    duration: 2000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Verification failed:', error);
            toast({
                title: 'Verification failed',
                description: 'Unable to verify your upload.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const checkUserUploadCount = async () => {
        try {
            const refreshedUser = await refreshUser();

            if (refreshedUser) {
                setNoOfFileUploads(refreshedUser.noOfFileUploads);
            }
            return refreshedUser;
        } catch (error) {
            console.error('Error checking user upload count:', error);
            return null;
        }
    };

    const onboardingSteps = [
        {
            id: 'prepare',
            title: 'Prepare the source file',
            description: 'Use the template or example workbook to structure the required customer fields before upload.',
            icon: FiUploadCloud,
            status: files.length > 0 || uploadedData ? 'complete' : 'current',
        },
        {
            id: 'processing',
            title: 'Upload and validate',
            description: 'FinanSee checks the schema and all rows before you confirm the import.',
            icon: FiClock,
            status: uploadedData ? 'complete' : isUploading ? 'current' : files.length > 0 ? 'current' : 'upcoming',
        },
        {
            id: 'results',
            title: 'Review and continue',
            description: 'Inspect the preview, confirm the upload, and move into the dashboard and datasets area.',
            icon: FiBarChart2,
            status: uploadedData && !uploadError ? 'complete' : 'upcoming',
        },
    ];

    const getStepAccent = (status) => {
        if (status === 'complete') {
            return { bg: 'green.50', borderColor: 'green.200', badge: 'green', label: 'Complete' };
        }

        if (status === 'current') {
            return { bg: 'brand.tealSoft', borderColor: 'teal.100', badge: 'blue', label: 'Current' };
        }

        return { bg: 'white', borderColor: 'gray.200', badge: 'gray', label: 'Next' };
    };

    // Download sample file handler
    const handleDownloadFile = (fileUrl, fileName) => {
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleLoadDemoDataset = async () => {
        if (!hasDatasetUploadAccess) {
            toast({
                title: 'Demo dashboard is available',
                description: 'Open the dashboard to inspect the synthetic workspace without uploading a file.',
                status: 'info',
                duration: 4000,
                isClosable: true,
            });
            navigate('/dashboard');
            return;
        }

        try {
            setIsLoadingDemoDataset(true);
            setUploadError(null);

            const response = await fetch(demoTrialSampleAssetPath);
            if (!response.ok) {
                throw new Error('Unable to fetch demo dataset');
            }

            const blob = await response.blob();
            const demoFile = new File(
                [blob],
                demoTrialSampleFileName,
                {
                    type: blob.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                }
            );

            setFiles([demoFile]);
            await uploadFileToAPI(demoFile);
        } catch (error) {
            console.error('Error loading demo dataset:', error);
            toast({
                title: 'Demo dataset unavailable',
                description: 'We could not preload the synthetic customer dataset. Please try again.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsLoadingDemoDataset(false);
        }
    };

    // Format value for display
    const formatValue = (value) => {
        if (value === null || value === undefined) return '-';
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        if (typeof value === 'number') return value.toLocaleString();
        return value.toString();
    };

    return (
        <Box maxW="100%" mx="auto" p={{ base: 4, sm: 6, md: 8 }} w="full">
            <Box bg="white" borderRadius="xl" boxShadow="lg" p={8} w="full">
                <Text fontSize={{ base: 'xl', sm: '2xl' }} fontWeight="bold" mb={1}>
                    Upload your customers file
                </Text>
                <Text fontSize={{ base: 'sm', sm: 'md' }} color="gray.600" mb={6}>
                    Import a CSV or Excel file to generate product recommendations per customer.
                </Text>

                <Grid templateColumns={{ base: '1fr', lg: 'repeat(3, 1fr)' }} gap={4} mb={6}>
                    {onboardingSteps.map((step, index) => {
                        const accent = getStepAccent(step.status);

                        return (
                            <Box
                                key={step.id}
                                bg={accent.bg}
                                border="1px solid"
                                borderColor={accent.borderColor}
                                borderRadius="xl"
                                p={4}
                            >
                                <HStack justify="space-between" align="start" mb={3}>
                                    <Box
                                        w={10}
                                        h={10}
                                        borderRadius="full"
                                        bg="white"
                                        border="1px solid"
                                        borderColor={accent.borderColor}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                    >
                                        <Icon as={step.icon} color={accent.badge === 'gray' ? 'gray.500' : `${accent.badge}.500`} boxSize={4.5} />
                                    </Box>
                                    <Badge colorScheme={accent.badge} borderRadius="full">
                                        {accent.label}
                                    </Badge>
                                </HStack>
                                <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={2}>
                                    Step {index + 1}
                                </Text>
                                <Text fontWeight="semibold" color="gray.800" mb={2}>
                                    {step.title}
                                </Text>
                                <Text fontSize="sm" color="gray.600">
                                    {step.description}
                                </Text>
                            </Box>
                        );
                    })}
                </Grid>

                <Box
                    bg="linear-gradient(135deg, rgba(15,118,110,0.08) 0%, rgba(240,253,250,1) 52%, rgba(255,255,255,1) 100%)"
                    borderRadius="xl"
                    border="1px solid"
                    borderColor="teal.100"
                    p={5}
                    mb={6}
                >
                    <Grid templateColumns={{ base: '1fr', xl: '1.1fr 0.9fr' }} gap={5}>
                        <Box>
                            <Badge colorScheme="teal" borderRadius="full" px={3} py={1} mb={3}>
                                Demo mode
                            </Badge>
                            <Text fontSize="lg" fontWeight="bold" color="gray.800" mb={3}>
                                Start with a ready-made synthetic customer base
                            </Text>
                            <Text fontSize="sm" color="gray.600" mb={4} maxW="2xl">
                                For sales calls or internal rehearsals, you can preload a synthetic workbook and move directly into
                                the normal FinanSee onboarding flow without preparing a file manually first.
                            </Text>
                            <HStack spacing={3} flexWrap="wrap" mb={4}>
                                <Badge colorScheme="teal" fontSize="sm">{demoTrialSampleCount} synthetic clients</Badge>
                                <Badge colorScheme="blue" fontSize="sm">{demoDatasetSummary.columnsCount} fields</Badge>
                            </HStack>
                            <HStack spacing={3} flexWrap="wrap">
                                <Button
                                    colorScheme="teal"
                                    onClick={handleLoadDemoDataset}
                                    isLoading={isLoadingDemoDataset || isUploading}
                                    loadingText="Loading demo dataset..."
                                >
                                    Load demo dataset
                                </Button>
                                <Button
                                    variant="outline"
                                    colorScheme="teal"
                                    onClick={() => handleDownloadFile(demoTrialSampleAssetPath, demoTrialSampleFileName)}
                                >
                                    Download demo file
                                </Button>
                            </HStack>
                        </Box>
                        <Box
                            bg="white"
                            borderRadius="xl"
                            border="1px solid"
                            borderColor="teal.100"
                            p={4}
                        >
                            <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={3}>
                                Full workspace spotlight
                            </Text>
                            <VStack align="stretch" spacing={3}>
                                {demoSpotlightCustomers.slice(0, 3).map((customer) => (
                                    <Box key={customer.customerId}>
                                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                            {customer.customerId} · {customer.title}
                                        </Text>
                                        <Text fontSize="xs" color="gray.600">
                                            {customer.angle}
                                        </Text>
                                    </Box>
                                ))}
                            </VStack>
                        </Box>
                    </Grid>
                </Box>

                {/* Required Fields Info */}
                {noOfFileUploads !== 1 && (
                    <Box mb={6}>
                        <Accordion allowToggle>
                            <AccordionItem border="none">
                                <AccordionButton
                                    onClick={() => setShowRequiredFields(!showRequiredFields)}
                                    bg="brand.tealSoft"
                                    borderRadius="lg"
                                    p={4}
                                    _hover={{ bg: 'brand.divider' }}
                                    _expanded={{ bg: 'brand.divider' }}
                                >
                                    <HStack flex="1" textAlign="left">
                                        <Icon as={FiInfo} color="brand.navy" boxSize={5} />
                                        <VStack align="start" spacing={0}>
                                            <Text fontSize="md" fontWeight="bold" color="blue.900">
                                                File Format Requirements
                                            </Text>
                                            <Text fontSize="sm" color="brand.navy2">
                                                9 core fields required • 19 optional fields for enhanced recommendations
                                            </Text>
                                        </VStack>
                                    </HStack>
                                    <AccordionIcon color="brand.navy" />
                                </AccordionButton>
                                <AccordionPanel pb={4} pt={6}>
                                    {/* Download Sample Files */}
                                    <Box bg="brand.tealSoft" borderRadius="lg" p={5} mb={6} border="1px solid" borderColor="teal.100">
                                        <HStack mb={3} spacing={2}>
                                            <Icon as={FiDownload} color="brand.navy" boxSize={5} />
                                            <Text fontSize="md" fontWeight="bold" color="blue.900">
                                                Download Sample Files
                                            </Text>
                                        </HStack>
                                        <Text fontSize="sm" color="gray.700" mb={4}>
                                            Get started quickly with our sample files. Download the template to fill in your own data, or use the example file to see the expected format.
                                        </Text>
                                        <HStack spacing={3} flexWrap="wrap">
                                            <Button
                                                size="md"
                                                colorScheme="blue"
                                                variant="solid"
                                                leftIcon={<FiDownload />}
                                                onClick={() => handleDownloadFile(templateFile, 'customer_data_template.xlsx')}
                                            >
                                                Empty Template
                                            </Button>
                                            <Button
                                                size="md"
                                                colorScheme="blue"
                                                variant="outline"
                                                leftIcon={<FiDownload />}
                                                onClick={() => handleDownloadFile(exampleFile, 'customer_data_example.xlsx')}
                                            >
                                                Pre-filled Example (15 customers)
                                            </Button>
                                        </HStack>
                                    </Box>

                                    {/* Core Required Fields */}
                                    <Box mb={6}>
                                        <HStack mb={4} spacing={2}>
                                            <Badge colorScheme="red" fontSize="sm" px={3} py={1}>
                                                REQUIRED
                                            </Badge>
                                            <Text fontSize="lg" fontWeight="bold" color="gray.800">
                                                9 Core Fields (Compulsory)
                                            </Text>
                                        </HStack>
                                        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={4}>
                                            {Object.entries(coreRequiredFields).map(([category, fields]) => (
                                                <GridItem key={category}>
                                                    <Box
                                                        bg="red.50"
                                                        border="2px solid"
                                                        borderColor="red.300"
                                                        borderRadius="lg"
                                                        p={4}
                                                        height="full"
                                                    >
                                                        <Text fontSize="sm" fontWeight="bold" color="red.800" mb={3}>
                                                            {category}
                                                        </Text>
                                                        <VStack align="start" spacing={2}>
                                                            {fields.map((field) => (
                                                                <Box key={field.name} width="full">
                                                                    <HStack spacing={2} mb={1}>
                                                                        <Tag
                                                                            size="sm"
                                                                            colorScheme="red"
                                                                            variant="solid"
                                                                            borderRadius="md"
                                                                        >
                                                                            <TagLabel fontSize="xs" fontWeight="bold">
                                                                                {field.name}
                                                                            </TagLabel>
                                                                        </Tag>
                                                                        <Badge colorScheme="red" fontSize="xs">
                                                                            Required
                                                                        </Badge>
                                                                    </HStack>
                                                                    <Text fontSize="xs" color="gray.700" pl={1}>
                                                                        {field.description}
                                                                    </Text>
                                                                </Box>
                                                            ))}
                                                        </VStack>
                                                    </Box>
                                                </GridItem>
                                            ))}
                                        </Grid>
                                    </Box>

                                    <Divider my={6} />

                                    {/* Optional Fields */}
                                    <Box>
                                        <HStack mb={4} spacing={2}>
                                            <Badge colorScheme="green" fontSize="sm" px={3} py={1}>
                                                OPTIONAL
                                            </Badge>
                                            <Text fontSize="lg" fontWeight="bold" color="gray.800">
                                                19 Additional Fields (For Enhanced Recommendations)
                                            </Text>
                                        </HStack>
                                        <Text fontSize="sm" color="gray.600" mb={4}>
                                            Including these fields will improve recommendation accuracy, but they are not required for basic functionality.
                                        </Text>
                                        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={4}>
                                            {Object.entries(optionalFields).map(([category, fields]) => (
                                                <GridItem key={category}>
                                                    <Box
                                                        bg="white"
                                                        border="1px solid"
                                                        borderColor="gray.300"
                                                        borderRadius="lg"
                                                        p={4}
                                                        height="full"
                                                    >
                                                        <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={3}>
                                                            {category}
                                                        </Text>
                                                        <VStack align="start" spacing={2}>
                                                            {fields.map((field) => (
                                                                <Box key={field.name} width="full">
                                                                    <HStack spacing={2} mb={1}>
                                                                        <Tag
                                                                            size="sm"
                                                                            colorScheme="gray"
                                                                            variant="subtle"
                                                                            borderRadius="md"
                                                                        >
                                                                            <TagLabel fontSize="xs" fontWeight="medium">
                                                                                {field.name}
                                                                            </TagLabel>
                                                                        </Tag>
                                                                        <Badge colorScheme="green" fontSize="xs" variant="subtle">
                                                                            Optional
                                                                        </Badge>
                                                                    </HStack>
                                                                    <Text fontSize="xs" color="gray.600" pl={1}>
                                                                        {field.description}
                                                                    </Text>
                                                                </Box>
                                                            ))}
                                                        </VStack>
                                                    </Box>
                                                </GridItem>
                                            ))}
                                        </Grid>
                                    </Box>
                                </AccordionPanel>
                            </AccordionItem>
                        </Accordion>
                    </Box>
                )}


                {/* Upload Area */}
                {noOfFileUploads !== 1 && (
                    <Box
                        border="2px dashed"
                        borderColor={isUploading ? 'brand.teal' : 'gray.300'}
                        borderRadius="lg"
                        p={12}
                        textAlign="center"
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        mb={4}
                        cursor={isUploading ? 'not-allowed' : 'pointer'}
                        _hover={{ borderColor: 'brand.teal', bg: 'gray.50' }}
                        onClick={() => !isUploading && fileInputRef.current?.click()}
                        maxW="full"
                        opacity={isUploading ? 0.6 : 1}
                        pointerEvents={isUploading ? 'none' : 'auto'}
                    >
                        {isUploading ? (
                            <Spinner size="xl" color="brand.teal" thickness="4px" mb={4} />
                        ) : (
                            <Icon as={FiUploadCloud} boxSize={10} color="gray.400" mb={4} />
                        )}
                        <Text fontSize="md" mb={2}>
                            {isUploading ? 'Uploading...' : 'Drag & drop your CSV/XLSX here'}
                        </Text>
                        {!isUploading && (
                            <>
                                <Text fontSize="sm" color="gray.500" mb={4}>
                                    or
                                </Text>
                                <Button colorScheme="blue" size="md">
                                    Browse files
                                </Button>
                                <Text fontSize="xs" color="gray.500" mt={3}>
                                    Accepted: .csv, .xlsx
                                </Text>
                            </>
                        )}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,.xlsx"
                            onChange={handleFileUpload}
                            style={{ display: 'none' }}
                            disabled={isUploading || isChecking}
                        />
                    </Box>
                )}

                {isChecking && <HStack mb={4}><Spinner /><Text>Checking rows and evidence coverage. No import has started.</Text></HStack>}
                {preflight && <ImportPreflight filename={preflight.file.name} report={preflight.report} isImporting={isUploading}
                    isRechecking={isChecking}
                    onRecheck={(mapping) => checkFileBeforeImport(preflight.file, mapping)}
                    onSaveMapping={async (name, columns, mapping) => {
                        try {
                            const {data} = await axios.post(`${API_BASE_URL}/content/import-mappings`, {name, columns, mapping},
                                {headers: {Authorization: `Bearer ${token}`}});
                            return data;
                        } catch (error) {
                            toast({title: 'Mapping not saved', description: error.response?.data?.message || 'Please try again.', status: 'error', duration: 5000, isClosable: true});
                            return null;
                        }
                    }}
                    onCancel={() => {preflightRequest.current += 1; setPreflight(null); setFiles([]); if (fileInputRef.current) fileInputRef.current.value = '';}}
                    onConfirm={async (mapping) => {
                        try {
                            const result = await uploadFileToAPI(preflight.file, mapping);
                            if (result) setPreflight(null);
                        } catch (_) { /* The upload handler preserves the error and selected file. */ }
                    }} />}

                {/* Info Box */}
                {noOfFileUploads !== 1 && (
                    <Box bg="brand.tealSoft" borderRadius="md" p={4} mb={4}>
                        <Text fontSize="sm" fontWeight="semibold" color="blue.900" mb={1}>
                            File must include the 9 core required fields
                        </Text>
                        <Text fontSize="xs" color="brand.navy2">
                            Optional fields can enhance recommendation quality but are not mandatory for processing. You can upload a new dataset with additional features later in the datasets tab.
                        </Text>
                    </Box>
                )}


                {/* Declaration */}
                {uploadedData && !isUploading && !uploadError && (
                    <Alert
                        status="success"
                        variant="subtle"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        textAlign="center"
                        borderRadius="md"
                        py={6}
                        mb={6}
                    >
                        <AlertIcon boxSize="30px" mr={0} />
                        <AlertTitle mt={2} mb={1} fontSize="lg" fontWeight="bold">
                            File Uploaded Successfully
                        </AlertTitle>
                        <AlertDescription maxWidth="sm" fontSize="sm" color="gray.700">
                            Your file has been uploaded and saved successfully. You can preview or delete the file on this screen,
                            or visit the <b>Datasets</b> tab on your dashboard.
                        </AlertDescription>
                    </Alert>
                )}

                {/* Uploaded Files */}
                {files.length > 0 && (
                    <HStack spacing={4} mb={4} flexWrap="wrap">
                        {files.map((file, index) => (
                            <HStack
                                key={index}
                                bg="gray.50"
                                px={3}
                                py={2}
                                borderRadius="md"
                                spacing={2}
                                maxW="100%"
                            >
                                <Icon as={FiFile} color="gray.600" />
                                <Text fontSize="sm" fontWeight="medium">
                                    {file.name}
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                    • {(file.size / 1024).toFixed(0)}KB
                                </Text>
                            </HStack>
                        ))}
                    </HStack>
                )}

                {/* Upload Progress */}
                {isUploading && (
                    <Box mb={4}>
                        <HStack justify="space-between" mb={2}>
                            <Text fontSize="sm" fontWeight="medium">
                                Uploading file...
                            </Text>
                            {/*<Text fontSize="sm" fontWeight="semibold" color="brand.navy">
                                {uploadProgress}%
                            </Text>*/}
                        </HStack>
                        <Progress
                            value={uploadProgress}
                            colorScheme="blue"
                            borderRadius="full"
                            size="sm"
                            hasStripe
                            isAnimated
                        />
                        <Text fontSize="xs" color="gray.600" mt={2}>
                            Processing your file... This may take a few moments.
                        </Text>
                    </Box>
                )}

                {/* Error Alert */}
                {uploadError && (
                    <Alert status="error" borderRadius="md" mb={4}>
                        <AlertIcon as={FiAlertCircle} />
                        <Box>
                            <AlertDescription fontSize="xs">
                                {uploadError}
                            </AlertDescription>
                        </Box>
                    </Alert>
                )}

                {/* File Info */}
                {uploadedData?.preview && !isUploading && (
                    <Box bg="gray.50" borderRadius="md" p={4} mb={4}>
                        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                            <Box>
                                <Text fontSize="xs" color="gray.600">Filename</Text>
                                <Text fontSize="sm" fontWeight="semibold">{uploadedData.preview.filename}</Text>
                            </Box>
                            <Box>
                                <Text fontSize="xs" color="gray.600">Total Rows</Text>
                                <Text fontSize="sm" fontWeight="semibold">{uploadedData.preview.total_rows}</Text>
                            </Box>
                            <Box>
                                <Text fontSize="xs" color="gray.600">Total Columns</Text>
                                <Text fontSize="sm" fontWeight="semibold">{uploadedData.preview.total_columns}</Text>
                            </Box>
                            <Box>
                                <Text fontSize="xs" color="gray.600">File Size</Text>
                                <Text fontSize="sm" fontWeight="semibold">
                                    {(uploadedData.preview.file_size / 1024).toFixed(2)} KB
                                </Text>
                            </Box>
                        </Grid>
                    </Box>
                )}



                {/* Action Buttons */}
                {files.length > 0 && !isUploading && (
                    <HStack spacing={3} mb={6}>
                        <Button
                            variant="ghost"
                            size="md"
                            onClick={handleRemoveAll}
                            leftIcon={<FiX />}
                            border="1px solid black"
                            isLoading={isDeleting}
                            loadingText="Removing..."
                        >
                            Remove File
                        </Button>
                    </HStack>
                )}

                {/* Preview Section */}
                {showPreview && uploadedData?.preview?.preview_rows && !isUploading && (
                    <>
                        <Text fontSize="lg" fontWeight="semibold" mb={4}>
                            Preview (first {uploadedData.preview.preview_rows.length} customers) (limited to 9 columns preview only)
                        </Text>

                        <Box overflowX="auto" mb={6}>
                            <Table variant="simple" size="sm">
                                <Thead bg="brand.tealSoft">
                                    <Tr>
                                        <Th color="blue.900" fontSize="xs" fontWeight="semibold">Customer ID</Th>
                                        <Th color="blue.900" fontSize="xs" fontWeight="semibold">Country</Th>
                                        <Th color="blue.900" fontSize="xs" fontWeight="semibold">Age</Th>
                                        <Th color="blue.900" fontSize="xs" fontWeight="semibold">Risk Profile</Th>
                                        <Th color="blue.900" fontSize="xs" fontWeight="semibold">Experience</Th>
                                        <Th color="blue.900" fontSize="xs" fontWeight="semibold">Cash %</Th>
                                        <Th color="blue.900" fontSize="xs" fontWeight="semibold">ETF %</Th>
                                        <Th color="blue.900" fontSize="xs" fontWeight="semibold">Stock %</Th>
                                        <Th color="blue.900" fontSize="xs" fontWeight="semibold">Leverage</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {uploadedData.preview.preview_rows.map((row, index) => (
                                        <Tr key={index} _hover={{ bg: 'gray.50' }}>
                                            <Td>
                                                <Text fontSize="sm" fontWeight="semibold">
                                                    {formatValue(row.customer_id)}
                                                </Text>
                                            </Td>
                                            <Td>
                                                <Badge colorScheme="blue" fontSize="xs">
                                                    {formatValue(row.country_iso)}
                                                </Badge>
                                            </Td>
                                            <Td>
                                                <Text fontSize="sm">{formatValue(row.age)}</Text>
                                            </Td>
                                            <Td>
                                                <Badge
                                                    colorScheme={row.risk_profile >= 7 ? 'red' : row.risk_profile >= 4 ? 'yellow' : 'green'}
                                                    fontSize="xs"
                                                >
                                                    {formatValue(row.risk_profile)}
                                                </Badge>
                                            </Td>
                                            <Td>
                                                <Text fontSize="sm">{formatValue(row.experience_years)}</Text>
                                            </Td>
                                            <Td>
                                                <Text fontSize="sm">{formatValue(row.cash_pct)}%</Text>
                                            </Td>
                                            <Td>
                                                <Text fontSize="sm">{formatValue(row.etf_pct)}%</Text>
                                            </Td>
                                            <Td>
                                                <Text fontSize="sm">{formatValue(row.single_stock_pct)}%</Text>
                                            </Td>
                                            <Td>
                                                <Text fontSize="sm">{formatValue(row.leverage_ratio)}</Text>
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </Box>
                    </>
                )}

                {/* Continue Button - Shows after successful upload */}
                {uploadedData && !isUploading && !uploadError && (
                    <HStack spacing={3} mt={6}>
                        <Button colorScheme="blue" size="md" onClick={handleVerifyAndContinue}>
                            Continue
                        </Button>
                    </HStack>
                )}
            </Box>
        </Box>
    );
}

export default UploadYourFile;
